import { NotificationTarget } from '@/types/notifications'

type CommentTagType = 'user' | 'position'

type CommentSegment =
  | { type: 'text'; text: string }
  | { type: CommentTagType; id: string; name: string }

const TAG_PART = '[^<>|]+'

function tagMarkup(type: CommentTagType) {
  return `<__display__|${type}=__id__>`
}

function tagRegex(type: string, flags?: string) {
  return new RegExp(`<(${TAG_PART})\\|${type}=(${TAG_PART})>`, flags)
}

export const USER_TAG_MARKUP = tagMarkup('user')
export const POSITION_TAG_MARKUP = tagMarkup('position')

export const USER_TAG_REGEX = tagRegex('user')
export const POSITION_TAG_REGEX = tagRegex('position')

const COMMENT_TAG_REGEX = tagRegex('(user|position)', 'g')

export function toTagDisplay(name: string): string {
  return name.replace(/[<>|]/g, '').trim()
}

export function parseCommentBody(body: string): CommentSegment[] {
  const segments: CommentSegment[] = []
  let lastIndex = 0

  Array.from(body.matchAll(COMMENT_TAG_REGEX)).forEach((match) => {
    const [raw, name, type, id] = match
    if (match.index > lastIndex) {
      segments.push({ type: 'text', text: body.slice(lastIndex, match.index) })
    }
    segments.push({ type: type as CommentTagType, id, name })
    lastIndex = match.index + raw.length
  })

  if (lastIndex < body.length) {
    segments.push({ type: 'text', text: body.slice(lastIndex) })
  }

  return segments
}

export function getMentionTargets(
  body: string,
  documentId: string,
): NotificationTarget[] {
  const targets = new Map<string, NotificationTarget>()
  parseCommentBody(body).forEach((segment) => {
    if (segment.type === 'user') {
      targets.set(`user:${segment.id}`, { kind: 'user', user_id: segment.id })
    } else if (segment.type === 'position') {
      targets.set(`position:${segment.id}`, {
        kind: 'position',
        position_id: segment.id,
        document_id: documentId,
      })
    }
  })
  return Array.from(targets.values())
}

export function renderCommentText(body: string): string {
  return parseCommentBody(body)
    .map((segment) =>
      segment.type === 'text' ? segment.text : `@${segment.name}`,
    )
    .join('')
}

type TagPiece = {
  start: number
  end: number
  visible: string
  raw: string
  isTag: boolean
}

function toPieces(value: string): TagPiece[] {
  let position = 0
  return parseCommentBody(value).map((segment) => {
    const isTag = segment.type !== 'text'
    const visible = isTag ? `@${segment.name}` : segment.text
    const raw = isTag
      ? `<${segment.name}|${segment.type}=${segment.id}>`
      : segment.text
    const piece = {
      start: position,
      end: position + visible.length,
      visible,
      raw,
      isTag,
    }
    position = piece.end
    return piece
  })
}

function keepRange(pieces: TagPiece[], from: number, to: number): string {
  return pieces
    .map((piece) => {
      const start = Math.max(piece.start, from)
      const end = Math.min(piece.end, to)
      if (start >= end) return ''
      const whole = start === piece.start && end === piece.end
      return piece.isTag && whole
        ? piece.raw
        : piece.visible.slice(start - piece.start, end - piece.start)
    })
    .join('')
}

export function applyPlainTextEdit(
  previousValue: string,
  nextPlainText: string,
): string {
  // The user edited the text they can see, such as "@Priya Nair". We compare the
  // old and new visible text to find the one changed range. Tags fully outside
  // that range keep their raw markup. A tag the edit cuts into becomes plain
  // text, because a half-edited tag no longer names a person or position.
  const pieces = toPieces(previousValue)
  const previousText = pieces.map((piece) => piece.visible).join('')
  const shortest = Math.min(previousText.length, nextPlainText.length)

  let prefix = 0
  while (prefix < shortest && previousText[prefix] === nextPlainText[prefix]) {
    prefix += 1
  }
  let suffix = 0
  while (
    suffix < shortest - prefix &&
    previousText[previousText.length - 1 - suffix] ===
      nextPlainText[nextPlainText.length - 1 - suffix]
  ) {
    suffix += 1
  }

  const inserted = nextPlainText.slice(prefix, nextPlainText.length - suffix)
  return (
    keepRange(pieces, 0, prefix) +
    inserted +
    keepRange(pieces, previousText.length - suffix, previousText.length)
  )
}

export type TagCandidate = {
  type: CommentTagType
  id: string
  display: string
}

const NON_WORD = /[^\p{L}\p{N}_]/u
const NON_ALPHANUMERIC = /[^\p{L}\p{N}]/u

// Lowercasing can change a string's length ("İ" becomes two characters), which
// would throw off where a name ends. Comparing with a collator ignores case but
// keeps accents, and the strings keep their own lengths.
function sameIgnoringCase(a: string, b: string) {
  return (
    a.length === b.length &&
    a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0
  )
}

// typedLength is how many characters after the "@" count as typed so far, or
// null on the final pass, where a name can be matched in full.
function findTagMatch(
  rest: string,
  candidates: TagCandidate[],
  typedLength: number | null,
): TagCandidate | null {
  const matches = candidates.filter((candidate) => {
    const name = candidate.display
    if (!name || !sameIgnoringCase(rest.slice(0, name.length), name)) {
      return false
    }
    if (typedLength !== null && name.length >= typedLength) return false
    const next = rest[name.length]
    return next === undefined
      ? typedLength === null
      : NON_ALPHANUMERIC.test(next)
  })
  if (matches.length === 0) return null

  const longest = matches.reduce((best, candidate) =>
    candidate.display.length > best.display.length ? candidate : best,
  )
  const sameName = candidates.filter((candidate) =>
    sameIgnoringCase(candidate.display, longest.display),
  )
  if (sameName.length > 1) return null

  // If a longer name starts with what the user has typed after the "@", they
  // may still be typing it. For example "@Ann" must wait if "Ann Lee" is also
  // a name.
  const typed = typedLength === null ? '' : rest.slice(0, typedLength)
  const couldGrow =
    typedLength !== null &&
    candidates.some(
      (candidate) =>
        candidate.display.length > longest.display.length &&
        sameIgnoringCase(candidate.display.slice(0, typed.length), typed),
    )
  return couldGrow ? null : longest
}

function autoTagText(
  text: string,
  candidates: TagCandidate[],
  caret: number | null,
): string {
  let result = ''
  let index = 0

  while (index < text.length) {
    const atCharacter = text[index]
    const startsMention =
      atCharacter === '@' && (index === 0 || NON_WORD.test(text[index - 1]))
    let typedLength: number | null = null
    if (caret !== null) {
      typedLength = index < caret ? caret - index - 1 : text.length - index - 1
    }
    const match = startsMention
      ? findTagMatch(text.slice(index + 1), candidates, typedLength)
      : null

    if (match) {
      result += `<${match.display}|${match.type}=${match.id}>`
      index += 1 + match.display.length
    } else {
      result += atCharacter
      index += 1
    }
  }

  return result
}

// While typing (final is false), caret is the cursor's offset in the rendered
// text and defaults to the end. An "@" before the cursor is checked only
// against the text up to the cursor, so a name the user is still typing is not
// tagged early. An "@" after the cursor is checked against all the text after
// it. Either way, a name at the very end is only tagged on the final pass.
export function autoTagExactNames(
  value: string,
  candidates: TagCandidate[],
  { final, caret }: { final: boolean; caret?: number },
): string {
  if (!value.includes('@') || candidates.length === 0) return value
  const pieces = toPieces(value)
  const renderedLength = pieces.length ? pieces[pieces.length - 1].end : 0
  const cursor = caret ?? renderedLength
  return pieces
    .map((piece) =>
      piece.isTag
        ? piece.raw
        : autoTagText(
            piece.raw,
            candidates,
            final ? null : cursor - piece.start,
          ),
    )
    .join('')
}
